import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { HOA_PERIOD_LABELS, LISTING_STATUS_LABELS } from "./constants";
import { SponsorCard } from "./SponsorCard";

const HERO_IMAGE_ANIMATION = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.6 } };

export function hoaLineFor(listing) {
  return listing.hoa_fee ? `${listing.hoa_fee}${HOA_PERIOD_LABELS[listing.hoa_period] || ""}` : "";
}

export function DetailsHero({ listing, ordered, hero, heroIndex, setHeroIndex, fileUrl, onViewPhoto }) {
  if (!ordered.length) return null;
  return (
    <section className="relative h-[42vh] sm:h-[56vh] lg:h-[480px] w-full overflow-hidden bg-[#111820]">
      <AnimatePresence mode="wait">
        {hero && (
          <motion.img key={hero.id} src={fileUrl(hero)} alt={listing.full_address || listing.project_name} onClick={() => onViewPhoto(heroIndex)}
            {...HERO_IMAGE_ANIMATION} className="absolute inset-0 h-full w-full object-cover cursor-pointer kb-zoom" data-testid="public-details-hero-image" />
        )}
      </AnimatePresence>
      {ordered.length > 1 && (
        <>
          <button data-testid="public-details-hero-prev" onClick={() => setHeroIndex((i) => (i - 1 + ordered.length) % ordered.length)} className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 hover:bg-white grid place-items-center transition-colors text-[#111820]"><ChevronLeft size={18} /></button>
          <button data-testid="public-details-hero-next" onClick={() => setHeroIndex((i) => (i + 1) % ordered.length)} className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 hover:bg-white grid place-items-center transition-colors text-[#111820]"><ChevronRight size={18} /></button>
          <div data-testid="public-details-hero-dots" className="absolute bottom-5 inset-x-0 flex justify-center gap-1.5">
            {ordered.map((f, i) => <button key={f.id} aria-label={`Photo ${i + 1}`} onClick={() => setHeroIndex(i)} className={`h-1.5 rounded-full transition-all ${i === heroIndex ? "w-5 bg-[#C7A361]" : "w-1.5 bg-white/60 hover:bg-white/90"}`} />)}
          </div>
        </>
      )}
    </section>
  );
}

export function DetailsTitle({ listing, onDownloadAll }) {
  return (
    <div className="flex items-start justify-between flex-wrap gap-4">
      <div>
        {listing.listing_status && <span data-testid="public-details-status" className="inline-block rounded-full bg-[#C7A361] text-[#111820] text-[11px] font-semibold uppercase tracking-[0.1em] px-3 py-1 mb-3">{LISTING_STATUS_LABELS[listing.listing_status] || listing.listing_status}</span>}
        <h1 data-testid="public-details-title" className="font-serif-display text-2xl sm:text-3xl text-[#202833]">Property Details</h1>
        <p className="mt-1.5 text-sm sm:text-base text-[#667085]">{listing.full_address || listing.project_name}</p>
      </div>
      <button data-testid="public-details-download-all" onClick={onDownloadAll} className="inline-flex items-center gap-1.5 rounded-full border border-[#C7A361] text-[#202833] text-xs font-semibold uppercase tracking-[0.08em] px-4 py-2 hover:bg-[#F8F7F4] transition shrink-0"><Download size={13} />Download All Photos</button>
    </div>
  );
}

export function FloorplanSection({ floorplanUrl }) {
  if (!floorplanUrl) return null;
  return (
    <section>
      <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833] mb-5">Floor Plan</h2>
      <div className="flex flex-col sm:flex-row items-start gap-5">
        <a href={floorplanUrl} target="_blank" rel="noopener noreferrer" data-testid="public-details-floorplan" className="block w-full max-w-md rounded-xl overflow-hidden border border-[#E7E4DE]">
          <img src={floorplanUrl} alt="Floorplan" className="w-full object-contain bg-[#F8F7F4]" />
        </a>
        <a href={floorplanUrl} target="_blank" rel="noopener noreferrer" data-testid="public-details-floorplan-button" className="inline-flex items-center justify-center rounded-full bg-[#C7A361] text-[#111820] text-xs font-semibold uppercase tracking-[0.08em] px-5 py-2.5 hover:brightness-95 transition shrink-0">View Full Floor Plan</a>
      </div>
    </section>
  );
}

export function VideoSection({ video, fileUrl }) {
  if (!video) return null;
  return (
    <section>
      <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833] mb-5">Walkthrough Video</h2>
      <div data-testid="public-details-video" className="relative aspect-video max-w-4xl rounded-xl overflow-hidden border border-[#E7E4DE] bg-[#111820]">
        {video.kind === "embed"
          ? <iframe data-testid="public-details-video-embed" src={video.embed_url} title="Walkthrough video" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full border-0" />
          : <video data-testid="public-details-video-file" controls playsInline className="absolute inset-0 w-full h-full object-contain" src={fileUrl({ id: video.file_id })} />}
      </div>
    </section>
  );
}

export function TourSection({ tourUrl }) {
  if (!tourUrl) return null;
  return (
    <section data-testid="public-details-tour" className="-mx-4 sm:-mx-6 lg:-mx-10">
      <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833] mb-5 px-4 sm:px-6 lg:px-10">3D Virtual Tour</h2>
      <iframe data-testid="public-details-tour-embed" src={tourUrl} title="3D virtual tour" allow="fullscreen; vr; xr-spatial-tracking" allowFullScreen loading="lazy" className="w-full h-[60vh] sm:h-[75vh] border-0" />
    </section>
  );
}

const FACT_PAIRS = [
  [["price", "List Price"], ["mls_number", "MLS #"]],
  [["listing_status", "Status"], ["property_type", "Property Type"]],
  [["beds", "Bedrooms"], ["baths", "Bathrooms"]],
  [["sqft", "Building Size"], ["year_built", "Year Built"]],
  [["stories", "Stories"], ["hoa", "Maintenance Fee"]],
];

export function FactsSection({ listing, hoaLine }) {
  const value = (key) => {
    if (key === "hoa") return hoaLine;
    if (key === "listing_status") return listing.listing_status ? LISTING_STATUS_LABELS[listing.listing_status] || listing.listing_status : "";
    if (key === "sqft") return listing.sqft ? `${listing.sqft} SqFt` : "";
    return listing[key];
  };
  const rows = FACT_PAIRS.filter(([a, b]) => value(a[0]) || value(b[0]));
  if (!rows.length) return null;
  return (
    <div>
      <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833] mb-5">Property Facts</h2>
      <dl data-testid="public-details-facts" className="divide-y divide-[#E7E4DE] border-t border-[#E7E4DE]">
        {rows.map(([[keyA, labelA], [keyB, labelB]]) => (
          <div key={keyA} className="grid sm:grid-cols-2 gap-x-8 py-3">
            {value(keyA) && (
              <div className="flex items-center justify-between">
                <dt className="text-sm text-[#667085]">{labelA}</dt>
                <dd data-testid={`public-details-fact-${keyA}`} className="text-sm font-medium text-[#202833]">{value(keyA)}</dd>
              </div>
            )}
            {value(keyB) && (
              <div className="flex items-center justify-between mt-2 sm:mt-0">
                <dt className="text-sm text-[#667085]">{labelB}</dt>
                <dd data-testid={`public-details-fact-${keyB}`} className="text-sm font-medium text-[#202833]">{value(keyB)}</dd>
              </div>
            )}
          </div>
        ))}
      </dl>
    </div>
  );
}

export function HighlightsSection({ highlights }) {
  if (!highlights?.length) return null;
  return (
    <div>
      <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833] mb-5">Property Highlights</h2>
      <ul data-testid="public-details-highlights" className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
        {highlights.map((h, i) => (
          <li key={`${i}-${h}`} data-testid={`public-details-highlight-${i}`} className="flex items-start gap-2 text-sm text-[#202833]"><Check size={15} className="mt-0.5 text-[#C7A361] shrink-0" strokeWidth={2.5} />{h}</li>
        ))}
      </ul>
    </div>
  );
}

export function DescriptionSection({ description }) {
  if (!description) return null;
  return (
    <div>
      <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833] mb-5">Description</h2>
      <p data-testid="public-details-description" className="text-sm text-[#667085] leading-relaxed whitespace-pre-line">{description}</p>
    </div>
  );
}

export function LocationSection({ listing }) {
  const cityState = listing.address_city_state_zip || "";
  if (!cityState && !listing.full_address) return null;
  const query = encodeURIComponent(listing.full_address || listing.project_name);
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${query}`;
  const embedUrl = `https://maps.google.com/maps?q=${query}&z=14&output=embed`;
  return (
    <section>
      <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833] mb-5">Location &amp; Neighborhood</h2>
      <div className="flex flex-col sm:flex-row gap-5 items-start">
        <div className="w-full sm:w-80 h-56 sm:h-48 rounded-xl overflow-hidden shrink-0 border border-[#E4DECF]">
          <iframe data-testid="public-details-map-embed" title="Property location map" src={embedUrl} className="w-full h-full grayscale-[15%]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </div>
        <div>
          <p className="font-serif-display text-lg text-[#202833]">{cityState || listing.full_address}</p>
          <p className="mt-2 text-sm text-[#667085] leading-relaxed">Close to local highways, shopping, dining, and everyday conveniences — a great home base for exploring the area.</p>
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="public-details-view-on-map" className="mt-3 inline-block text-sm font-medium text-[#C7A361] hover:underline">View on Map →</a>
        </div>
      </div>
    </section>
  );
}

export function PreferredPartnersCard({ sponsors, onInquire }) {
  if (!sponsors?.length) return null;
  return (
    <div data-testid="public-details-preferred-partners">
      <h2 className="font-serif-display text-lg text-[#202833] mb-3">Our Preferred Partners</h2>
      <div className="space-y-4">
        {sponsors.map((ad) => <SponsorCard key={ad.id} advertiser={ad} onInquire={onInquire} compact />)}
      </div>
    </div>
  );
}
